/**
 * Real AI Service — calls Lovable AI Gateway via edge function.
 * Falls back to local mock if API call fails.
 */

import type { AISettings } from '../types';
import { generateReply as mockGenerateReply, type AIResponse, type AITone } from './aiMockService';
import { supabase } from '@/integrations/supabase/client';
import { shopifyProductService } from './dataService';
import { getCurrentWorkspaceId } from './workspaceState';

export async function generateReplyReal(
  message: string,
  settings?: Partial<AISettings>,
  context?: {
    contactData?: any;
    orderData?: any;
    conversationHistory?: { direction: string; body: string; replyTo?: { direction: string; body: string } | null }[];
    tone?: string;
    aiMemory?: string;
    contactId?: string;
    conversationId?: string;
    imageUrl?: string;
  }
): Promise<AIResponse> {
  try {
    // Build contact context string
    let contactContext = '';
    if (context?.contactData) {
      const cd = context.contactData;
      const parts: string[] = [];
      if (cd.fullName) parts.push(`Customer name: ${cd.fullName}`);
      if (cd.tags?.length) parts.push(`Tags: ${cd.tags.join(', ')}`);
      if (cd.leadStatus) parts.push(`Lead status: ${cd.leadStatus}`);
      contactContext = parts.join('. ');
    }
    if (context?.orderData) {
      const od = context.orderData;
      const parts: string[] = [];
      if (od.orderStatus) parts.push(`Order status: ${od.orderStatus}`);
      if (od.productName) parts.push(`Product: ${od.productName}`);
      if (od.orderAmount) parts.push(`Amount: ${od.orderAmount}`);
      if (od.deliveryStatus) parts.push(`Delivery: ${od.deliveryStatus}`);
      contactContext += (contactContext ? '. ' : '') + parts.join('. ');
    }
    if (context?.aiMemory) {
      contactContext += (contactContext ? '. ' : '') + `Previous context: ${context.aiMemory}`;
    }

    // Inject Shopify product catalog into product context
    const shopifyCatalog = shopifyProductService.getAIContext();
    const fullProductContext = [settings?.productContext, shopifyCatalog].filter(Boolean).join('\n\n');

    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        message,
        imageUrl: context?.imageUrl || undefined,
        systemPrompt: settings?.systemPrompt,
        brandTone: settings?.brandTone || context?.tone || 'friendly',
        productContext: fullProductContext || undefined,
        temperature: settings?.temperature ?? 0.7,
        maxTokens: settings?.maxTokens ?? 1024,
        maxResponseLines: settings?.maxResponseLines ?? 5,
        conversationHistory: context?.conversationHistory,
        contactContext: contactContext || undefined,
        contactId: context?.contactId,
        conversationId: context?.conversationId,
        workspaceId: getCurrentWorkspaceId(),
      },
    });

    if (error) {
      throw new Error(error.message || 'Edge function error');
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    // Normalize the response to match AIResponse interface
    const result: AIResponse = {
      reply: data.reply || '',
      intent: data.intent || 'unknown',
      confidence: typeof data.confidence === 'number' ? data.confidence : 0.5,
      sentiment: (['positive', 'neutral', 'negative'].includes(data.sentiment) ? data.sentiment : 'neutral') as AIResponse['sentiment'],
      sentimentScore: typeof data.sentimentScore === 'number' ? data.sentimentScore : 0,
      language: data.language || 'English',
      shouldEscalate: Boolean(data.shouldEscalate),
      escalationReason: data.escalationReason || undefined,
      leadScore: typeof data.leadScore === 'number' ? data.leadScore : 30,
      leadCategory: (['cold', 'warm', 'hot'].includes(data.leadCategory) ? data.leadCategory : 'cold') as AIResponse['leadCategory'],
      suggestedReplies: Array.isArray(data.suggestedReplies) ? data.suggestedReplies : [],
      detectedTone: data.detectedTone || (settings?.brandTone || 'friendly'),
      orderRelated: Boolean(data.orderRelated),
    };

    return result;
  } catch (error: any) {
    console.warn('[AI] Real API failed, falling back to local mock:', error.message);
    // Fallback to local mock
    const mockCtx = context ? {
      ...context,
      conversationHistory: context.conversationHistory?.map(m => `${m.direction}: ${m.body}`),
      tone: (context.tone || 'friendly') as AITone,
    } : undefined;
    const fallback = mockGenerateReply(message, settings, mockCtx);
    // Tag it as fallback so UI can indicate
    (fallback as any)._fallback = true;
    (fallback as any)._error = error.message;
    return fallback;
  }
}
